/* Website inquiries use the explicitly configured hosted delivery endpoint. */
(() => {
  const endpoint = "https://api.web3forms.com/submit";
  const marker = "data-ss-contact-delivery-ready";
  if (
    document.documentElement.hasAttribute(marker) ||
    typeof window.fetch !== "function" ||
    typeof window.FormData !== "function" ||
    typeof window.AbortController !== "function"
  ) return;
  document.documentElement.setAttribute(marker, "true");

  const states = new WeakMap();
  const submitControls = 'button[type="submit"], button:not([type]), input[type="submit"], input[type="image"]';
  const failureMessage = "We couldn’t submit your message. Your answers are still here. Please try again.";

  // Display labels only; delivery validation, payload and acceptance stay shared.
  function messageFor(form, message) {
    try {
      const labels = JSON.parse(form.querySelector("[data-ss-contact-messages]")?.dataset.ssContactMessages || "{}");
      return Object.prototype.hasOwnProperty.call(labels, message) && typeof labels[message] === "string"
        ? labels[message] : message;
    } catch (_) { return message; }
  }

  function reveal(form) {
    const panel = form.querySelector("product-request-information") || form.closest("product-request-information");
    if (!panel) return;
    if (typeof panel.expand === "function") panel.expand();
    else {
      panel.setAttribute("expanded", "true");
      panel.querySelector(".product-request-information-toggle")?.setAttribute("aria-expanded", "true");
    }
  }

  function notice(form, kind, message, focus = false) {
    const selector = kind === "error" ? "[data-ss-contact-error]" : "[data-ss-contact-status]";
    let node = form.querySelector(selector);
    if (!node) {
      node = document.createElement("div");
      node.setAttribute(kind === "error" ? "data-ss-contact-error" : "data-ss-contact-status", "");
      form.append(node);
    }
    const other = form.querySelector(kind === "error" ? "[data-ss-contact-status]" : "[data-ss-contact-error]");
    if (other) {
      other.hidden = true;
      other.textContent = "";
    }
    node.setAttribute("role", kind === "error" ? "alert" : "status");
    node.setAttribute("tabindex", "-1");
    node.textContent = messageFor(form, message);
    node.hidden = false;
    if (focus) {
      reveal(form);
      node.focus({ preventScroll: true });
      node.scrollIntoView({ block: "nearest" });
    }
  }

  function firstValue(fields, keys) {
    for (const key of keys) {
      const entry = [...fields].find(([name]) => name.toLowerCase() === key);
      const value = entry?.[1].find((item) => item.trim());
      if (value) return value.trim();
    }
    return "";
  }

  function labelFor(name) {
    const label = name.replace(/[_-]+/g, " ");
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  function payloadFor(form) {
    const data = new FormData(form);
    const fields = new Map();
    for (const [name, value] of data) {
      if (typeof value !== "string") continue;
      const match = name.match(/^contact\[(.+?)\](?:\[\])?$/);
      if (!match) continue;
      if (!fields.has(match[1])) fields.set(match[1], []);
      fields.get(match[1]).push(value);
    }
    const email = firstValue(fields, ["email", "email-address"]);
    const emailInput = document.createElement("input");
    emailInput.type = "email";
    emailInput.required = true;
    emailInput.value = email;
    if (!emailInput.checkValidity()) throw new Error("invalid-email");
    const accessKey = String(data.get("access_key") || "").trim();
    if (!accessKey || data.get("botcheck")) throw new Error("unavailable");

    const body = [...fields]
      .filter(([name]) => name.toLowerCase() === "body")
      .flatMap(([, values]) => values)
      .join("\n\n")
      .trim();
    const details = [...fields]
      .filter(([name]) => name.toLowerCase() !== "body")
      .map(([name, values]) => labelFor(name) + ": " + values.map((value) => value || "(not provided)").join("; "))
      .join("\n");
    const message = (body || "New website inquiry.") + (details ? "\n\nInquiry details\n" + details : "");
    if (message.length > 100000) throw new Error("too-long");
    const header = (value, fallback, limit) => String(value || fallback).replace(/[\r\n]+/g, " ").trim().slice(0, limit);
    const payload = {
      access_key: accessKey,
      from_name: header(data.get("from_name"), "Saber Sales website", 100),
      subject: header(data.get("subject"), "Website contact request", 200),
      name: firstValue(fields, ["name", "full-name", "full name"]),
      email,
      message,
      botcheck: "",
    };
    const phone = firstValue(fields, ["phone", "phone-number", "phone number", "telephone"]);
    if (phone) payload.phone = phone;
    return payload;
  }

  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches("[data-ss-contact-delivery]") || event.defaultPrevented) return;
    event.preventDefault();
    const existing = states.get(form);
    if (existing?.pending || existing?.submitted) return;
    if (form.method !== "post" || form.action !== endpoint) {
      notice(form, "error", failureMessage, true);
      return;
    }
    if (!form.reportValidity()) {
      reveal(form);
      return;
    }
    let payload;
    try {
      payload = payloadFor(form);
    } catch (error) {
      const message = error.message === "invalid-email"
        ? "Please enter a valid email address so our team can reply. Your answers are still here."
        : error.message === "too-long"
          ? "Your message is too long to send. Please shorten it and try again."
          : failureMessage;
      notice(form, "error", message, true);
      return;
    }

    const controls = [...form.querySelectorAll(submitControls)].map((node) => ({
      node,
      disabled: node.disabled,
      content: node.tagName === "BUTTON" ? node.innerHTML : node.value,
    }));
    const busyBefore = form.getAttribute("aria-busy");
    states.set(form, { pending: true, submitted: false });
    form.dataset.ssContactState = "pending";
    form.setAttribute("aria-busy", "true");
    for (const { node } of controls) {
      node.disabled = true;
      if (node.tagName === "BUTTON") node.textContent = messageFor(form, "Sending…");
      else node.value = messageFor(form, "Sending…");
    }
    notice(form, "status", "Sending your message…");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    let submitted = false;
    try {
      const response = await window.fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "omit",
        cache: "no-store",
        redirect: "error",
        signal: controller.signal,
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result?.success !== true) throw new Error("not-accepted");
      submitted = true;
      states.set(form, { pending: false, submitted: true });
      form.dataset.ssContactState = "submitted";
      for (const { node } of controls) {
        if (node.tagName === "BUTTON") node.textContent = messageFor(form, "Submitted");
        else node.value = messageFor(form, "Submitted");
      }
      notice(form, "status", "Thank you. Your message has been submitted.", true);
    } catch (error) {
      states.delete(form);
      form.dataset.ssContactState = "error";
      notice(form, "error", error.name === "AbortError"
        ? "The connection timed out before we could confirm submission. Your answers are still here."
        : failureMessage, true);
    } finally {
      window.clearTimeout(timeout);
      if (busyBefore === null) form.removeAttribute("aria-busy");
      else form.setAttribute("aria-busy", busyBefore);
      if (!submitted) for (const { node, disabled, content } of controls) {
        node.disabled = disabled;
        if (node.tagName === "BUTTON") node.innerHTML = content;
        else node.value = content;
      }
    }
  });
})();
